import moment from 'moment';
import { useEffect, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import NavBar from 'components/pageLayout/navBar/NavBar';
import StyledDiv from 'components/styled/StyledDiv';
import Text from 'components/typography/Text';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';

const LeavingInBlock = styled.div`
  display: flex;
  justify-content: end;
  padding: 0 40px;
  max-height: 75px;
  @media (max-width: 1199.98px) {
    width: 100%;
    padding-top: 20px;
    padding-left: 16px;
    justify-content: start;
    margin-top: 0;
    margin-left: 1.5rem;
  }
`;

const LeavingInLeft = styled.div`
  background-color: ${(props) =>
    props.theme.secondaryColourNoFallBack || '#072636'};
  color: ${(props) => props.theme.primaryColour || 'white'};
  padding: 20px;
  border-top-left-radius: 40px;
  border-bottom-left-radius: 40px;
  margin-right: 2px;
  display: flex;
  align-items: center;
  @media (max-width: 1199.98px) {
    display: none;
  }
`;

const LeavingInRight = styled.div`
  background-color: ${(props) =>
    props.theme.secondaryColourNoFallBack || '#072636'};
  color: ${(props) => props.theme.primaryColour || 'white'};
  padding: 20px;
  border-top-right-radius: 40px;
  border-bottom-right-radius: 40px;
  display: flex;
  align-items: center;
  @media (max-width: 1199.98px) {
    border-top-left-radius: 40px;
    border-bottom-left-radius: 40px;
    padding: 6px 10px;
    justify-content: center;
  }
`;

const ClockSVG = styled.svg`
  width: 32px;
  height: 32px;
  fill: ${(props) => props.theme.primaryColour || 'white'};
  margin-right: 10px;
  @media (max-width: 1199.98px) {
    width: 28px;
    height: 28px;
    margin-right: 6px;
  }
`;

const ClockSVGPath = `
  M16.0001 29.3333C8.63608 29.3333 2.66675 23.364 2.66675 16C2.66675 8.636 8.63608 2.66666 16.0001 2.66666C23.3641 2.66666 29.3334 8.636 29.3334 16C29.3334 23.364 23.3641 29.3333 16.0001 29.3333ZM17.3334 16V9.33333H14.6667V18.6667H22.6667V16H17.3334Z
`;

export default function DefaultHeader({ label, departureDate, authorName, homeLink }) {
  const [countdownDisplay, setCountdownDisplay] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      const targetDate = moment(departureDate);
      const currentDate = moment();
      const duration = moment.duration(targetDate.diff(currentDate));

      const days = duration.asDays();
      const hours = duration.hours();
      setCountdownDisplay(`${Math.floor(days)} days : ${hours} hours`);
    };
    updateCountdown();
  }, [departureDate]);

  const {
    whiteLabelSettings: { primaryColour, secondaryColourNoFallBack } = {},
  } = useWhiteLabelSettings();

  const customStyles = `
    background: ${primaryColour || '#18A8CB'};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    @media (max-width: 1199.98px) {
      height: auto;
    }
  `;

  const theme = {
    primaryColour,
    secondaryColourNoFallBack,
  };

  return (
    <header>
      <ThemeProvider theme={theme}>
        <StyledDiv customStyles={customStyles}>
          <NavBar homeLink={homeLink} />
          <div className="flex flex-col-reverse justify-between items-center lg:flex-row">
            <div className="mx-8 my-2 md:my-7 self-start xl:m-8">
              <Text className="text-white font-medium break-words text-[35px] md:text-[35px] xl:text-[75px]">
                {label}
              </Text>
              {authorName && (
                <Text className="text-white text-[16px] md:text-[22px] font-light ml-0 mb-2 md:ml-2 md:mb-0">
                  Your concierge: {authorName}
                </Text>
              )}
            </div>
            {departureDate && (
              <LeavingInBlock>
                <LeavingInLeft>
                  <Text className="text-[14px] md:text-[21px] font-bold md:font-[600]">
                    Leaving in:
                  </Text>
                </LeavingInLeft>
                <LeavingInRight>
                  <ClockSVG
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d={ClockSVGPath} />
                  </ClockSVG>
                  <Text className="text-[14px] md:text-[21px] font-bold md:font-[600]">
                    {countdownDisplay}
                  </Text>
                </LeavingInRight>
              </LeavingInBlock>
            )}
          </div>
        </StyledDiv>
      </ThemeProvider>
    </header>
  );
}
